import type {
  FiltersInQuickSearch,
  FiltersInQuickSearchValues,
  FilterType,
  QuickSearchApiClient,
  QuickSearchFilters,
} from '@/scopes/search/quick-search/domain/types';
import type { QuickSearchStoreApi } from '@/scopes/search/quick-search/infrastructure/store/store';

export function quickSearchUseCases({
  quickSearchApiClient,
  store,
}: {
  quickSearchApiClient: QuickSearchApiClient;
  store: QuickSearchStoreApi;
}) {
  async function filterConfirmed({ type, selection }: { type: FilterType; selection: FiltersInQuickSearchValues }) {
    const { setIn, publicationCode, contextItemId, filterSelection } = store.getState();

    if (
      (filterSelection[type] === undefined && selection === undefined) ||
      JSON.stringify(filterSelection[type] ?? []) === JSON.stringify(selection)
    )
      return;

    const newSelection = {
      ...filterSelection,
      [type]: selection,
    };

    setIn('filterSelection', newSelection);
    setIn('getQuickSearch.loading', true);
    const { filterOptions, searchLink, totalResults } = await quickSearchApiClient.getQuickSearch({
      publicationCode,
      contextItemId,
      filterSelection: newSelection,
    });
    setIn('getQuickSearch.loading', false);
    setIn('searchLink', searchLink);
    setIn('totalResults', totalResults);
    setIn('filterOptions', filterOptions);
  }

  async function initQuickSearch({
    publicationCode,
    contextItemId,
    initialSelection,
  }: {
    publicationCode: string;
    contextItemId: string;
    initialSelection: QuickSearchFilters;
  }) {
    const { setIn } = store.getState();

    setIn('publicationCode', publicationCode);
    setIn('contextItemId', contextItemId);

    setIn('filterSelection', initialSelection);
    setIn('getQuickSearch.loading', true);
    const { filterOptions, searchLink, totalResults } = await quickSearchApiClient.getQuickSearch({
      publicationCode,
      contextItemId,
      filterSelection: initialSelection,
    });
    setIn('getQuickSearch.loading', false);
    setIn('searchLink', searchLink);
    setIn('totalResults', totalResults);
    setIn('filterOptions', filterOptions);
  }

  async function switchPublicationCode(publicationCode: string, selection: Partial<FiltersInQuickSearch>) {
    const { setIn, contextItemId } = store.getState();

    setIn('publicationCode', publicationCode);
    setIn('getQuickSearch.loading', true);
    setIn('filterSelection', selection);

    const { filterOptions, searchLink, totalResults } = await quickSearchApiClient.getQuickSearch({
      publicationCode,
      contextItemId,
      filterSelection: selection,
    });
    setIn('getQuickSearch.loading', false);
    setIn('searchLink', searchLink);
    setIn('totalResults', totalResults);
    setIn('filterOptions', filterOptions);
  }

  return {
    filterConfirmed,
    initQuickSearch,
    switchPublicationCode,
  };
}
